import { useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Phase 4 · Explicit human confirmation dialog.
 *
 * Props:
 *   open, onOpenChange
 *   actionId, description, payload (object)
 *   onConfirm (async)
 */
export default function ConfirmActionDialog({
  open,
  onOpenChange,
  actionId,
  description,
  payload,
  onConfirm,
}) {
  const [running, setRunning] = useState(false);
  const pending = useRef(false);

  const handleRun = async () => {
    if (pending.current) return;
    pending.current = true;
    setRunning(true);
    try {
      await onConfirm();
    } finally {
      pending.current = false;
      setRunning(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(nextOpen) => {
      if (!pending.current) onOpenChange(nextOpen);
    }}>
      <AlertDialogContent data-testid="ai-confirm-action-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Approve this action?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <div className="text-sm text-neutral-700">{description}</div>
              <div className="text-[11px] text-neutral-500 uppercase tracking-wide">
                Action
              </div>
              <div className="font-mono text-xs bg-neutral-50 border border-neutral-200 rounded-md p-2">
                {actionId}
              </div>
              <div className="text-[11px] text-neutral-500 uppercase tracking-wide">
                Material parameters
              </div>
              <pre
                data-testid="ai-confirm-action-payload"
                className="font-mono text-xs bg-neutral-50 border border-neutral-200 rounded-md p-2 overflow-x-auto whitespace-pre-wrap break-all"
              >
                {JSON.stringify(payload || {}, null, 2)}
              </pre>
              <div className="text-[11px] text-neutral-500">
                This approval authorises exactly these parameters. Any change
                before execution will invalidate the approval.
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid="ai-confirm-action-cancel"
            disabled={running}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="ai-confirm-action-run"
            disabled={running}
            onClick={(e) => {
              e.preventDefault();
              handleRun();
            }}
          >
            {running ? "Running…" : "Run now"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
